<template>
	<div>
		<div>
			<CInput
				v-model.trim="paymentMethod"
				placeholder="Search by payment method"
				class="flex-fill"
				autocomplete="off"
				data-test-id="input-payment-method"
			>
				<template #prepend-content>
					<CIcon class="cil-magnifying-glass" name="cil-magnifying-glass" />
				</template>
			</CInput>
		</div>
		<slot
			v-if="!filteredList.length"
			name="no-item"
		>
		</slot>
		<div v-show="filteredList.length" class="list">
			<div class="select-all mb-1 align-items-center">
				<CInputCheckbox
					v-indeterminate="globalIndeterminateState === 'indeterminated'"
					:checked.sync="isSelectedAll"
					custom
					label="Select all"
					class="select-all-input"
					add-label-classes="font-weight-bold"
					data-test-id="select-all-input"
					@update:checked="handleCheckedSelectAll"
				/>
			</div>
			<DynamicScroller
				ref="scroller"
				:items="filteredList"
				:min-item-size="35"
				key-field="id"
				class="scroller list-select"
			>
				<template v-slot="{ item, index, active }">
					<DynamicScrollerItem
						:item="item"
						:active="active"
						:data-index="index"
						class="list-item"
					>
						<div class="d-flex flex-column flex-fill">
							<div class="d-flex align-items-center">
								<div
									v-if="item.methods && item.methods.length"
									class="expand-toggle mr-2 cursor-pointer"
									@click.stop="handleToggleExpand(item)"
								>
									<template>
										<CIcon
											:name="item._isExpanded ? 'cil-chevron-bottom' : 'cil-chevron-right'"
											size="sm"
											class="color-black-45"
										/>
									</template>
								</div>
								<CInputCheckbox
									:id="`item-payment${item.id}`"
									v-indeterminate="item.isIndeterminate"
									:checked="item.isChecked"
									custom
									class="list-checkbox overflow-hidden flex-fill"
									@update:checked="handleCheckedPaymentMethod($event, item)"
								>
									<template #label>
										<label :for="`item-payment${item.id}`" class="custom-control-label list-label w-100 typo-body-2 font-weight-normal text-nowrap text-truncate color-black-45 mb-0 ml-2">
											<span class="list-payment-method color-black">{{ item.name }}</span>
										</label>
									</template>
								</CInputCheckbox>
							</div>
							<div
								v-if="item._isExpanded && item.methods && item.methods.length"
								class="child-list"
							>
								<div
									v-for="child in item.methods"
									:key="child.id"
									class="child-item"
								>
									<CInputCheckbox
										:id="`item-payment-child${child.id}`"
										:checked="child.isChecked"
										custom
										class="list-checkbox overflow-hidden"
										@update:checked="handleCheckedChildPaymentMethod($event, child)"
									>
										<template #label>
											<label :for="`item-payment-child${child.id}`" class="custom-control-label list-label w-100 typo-body-2 font-weight-normal text-nowrap text-truncate color-black-45 mb-0 ml-2">
												<span class="list-payment-method color-black">{{ child.name }}</span>
											</label>
										</template>
									</CInputCheckbox>
								</div>
							</div>
						</div>
					</DynamicScrollerItem>
				</template>
			</DynamicScroller>
		</div>
	</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { DynamicScroller, DynamicScrollerItem } from 'vue-virtual-scroller';
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';

export default {
	name: 'FormGetPaymentMethod',
	components: {
		DynamicScroller,
		DynamicScrollerItem,
	},
	directives: {
		indeterminate: {
			bind(el, binding) {
				const input = el.querySelector('input');
				if (input) input.indeterminate = Boolean(binding.value);
			},
			update(el, binding) {
				const input = el.querySelector('input');
				if (input) input.indeterminate = Boolean(binding.value);
			},
		},
	},
	props: {
		// Unified nested structure: Array of Groups, each with a 'methods' array of selected children.
		selectedList: {
			type: Array,
			default: () => [],
		},
	},
	data() {
		return {
			isSelectedAll: false,
			globalIndeterminateState: 'unchecked', // 'checked', 'unchecked', 'indeterminated'
			paymentMethod: '',
			list: [],
		};
	},
	computed: {
		...mapGetters({
			getPaymentMethodGroupList: 'payments/paymentMethodGroupList',
		}),
		filteredList() {
			if (!this.paymentMethod) {
				return this.list;
			}

			return this.list.filter((method) => method.name.toLowerCase().startsWith(this.paymentMethod.toLowerCase()));
		},
	},
	watch: {
		selectedList: {
			immediate: true,
			handler(val) {
				this.reconcileIncomingData(val);
			},
		},
	},
	async created() {
		// ตรวจสอบว่า groups ที่ load มาแล้วมี methods ข้างในไหม
		// กรณีที่ Orders.vue load groups มาก่อนโดยไม่มี methods จะต้อง fetch ใหม่พร้อม with_payment_methods=1
		const hasMethodsLoaded = this.getPaymentMethodGroupList.some((g) => g.methods?.length > 0);
		if (!hasMethodsLoaded) {
			await this.getPaymentMethodGroups({ with_payment_methods: 1 });
		}
		this.list = this.getPaymentMethodGroupList.map((item) => ({
			...item,
			isChecked: false,
			isIndeterminate: false,
			_isExpanded: false,
			methods: item.methods.map((child) => ({
				...child,
				isChecked: false,
			})),
		}));
	},
	methods: {
		...mapActions({
			getPaymentMethodGroups: 'payments/getPaymentMethodGroups',
		}),
		resetState() {
			this.paymentMethod = '';
			this.list.forEach((group) => {
				group.isChecked = false;
				group.isIndeterminate = false;
				(group.methods || []).forEach((child) => {
					child.isChecked = false;
				});
			});
			this.checkSelectionState();
			this.syncGroupIndeterminate();
		},
		reconcileIncomingData(incomingNested = []) {
			if (!this.list.length) return;

			// Populate internal isChecked flags by mapping across current list
			this.list.forEach((group) => {
				const matchedGroup = incomingNested.find((s) => s.id === group.id);
				const hasChildren = group.methods && group.methods.length > 0;

				if (hasChildren) {
					// Children determine parent check status
					(group.methods || []).forEach((child) => {
						// Logic for auto-selecting children:
						// 1. Specific child ID is in the nested list
						// 2. OR the group itself was matched but had no specific children listed (implies Full/Legacy selection)
						const isChildInNested = matchedGroup && matchedGroup.methods && matchedGroup.methods.some((s) => s.id === child.id);
						const isGroupSelectedAsWhole = matchedGroup && (!matchedGroup.methods || matchedGroup.methods.length === 0);

						child.isChecked = !!(isChildInNested || isGroupSelectedAsWhole);
					});
					this.updateParentSelectionState(group);
				} else {
					// No children: use matched status directly
					group.isChecked = !!matchedGroup;
					group.isIndeterminate = false;
				}
			});

			this.checkSelectionState();
			this.syncGroupIndeterminate();
		},

		/**
		 * Recalculates isChecked and isIndeterminate for a group based on its children's status.
		 */
		updateParentSelectionState(group) {
			if (!group.methods || !group.methods.length) return;

			const checkedCount = group.methods.filter((m) => m.isChecked).length;
			const totalCount = group.methods.length;

			group.isChecked = checkedCount === totalCount;
			group.isIndeterminate = checkedCount > 0 && checkedCount < totalCount;
		},

		checkSelectionState() {
			const allLeaves = this.filteredList.reduce((acc, group) => {
				if (group.methods && group.methods.length > 0) {
					return [...acc, ...group.methods];
				}
				// If the group has no methods, the group itself acts as the selectable leaf
				return [...acc, group];
			}, []);

			const checkedLeaves = allLeaves.filter((leaf) => leaf.isChecked);

			const isEverySelected = allLeaves.length > 0 && checkedLeaves.length === allLeaves.length;
			const isSomeSelected = allLeaves.length > 0 && checkedLeaves.length > 0;

			if (isEverySelected) {
				this.globalIndeterminateState = 'checked';
				this.isSelectedAll = true;
				return;
			}

			if (isSomeSelected) {
				this.globalIndeterminateState = 'indeterminated';
				this.isSelectedAll = true;
				return;
			}

			this.globalIndeterminateState = 'unchecked';
			this.isSelectedAll = false;
		},

		setSelectedAllState(state) {
			this.globalIndeterminateState = state;
			this.isSelectedAll = state !== 'unchecked';
		},
		syncGroupIndeterminate() {
			this.$nextTick(() => {
				if (this.$refs.scroller) {
					this.$refs.scroller.forceUpdate();
				}
			});
		},
		handleCheckedSelectAll(checked) {
			this.filteredList.forEach((group) => {
				group.isChecked = checked;
				group.isIndeterminate = false;
				(group.methods || []).forEach((child) => {
					child.isChecked = checked;
				});
			});
			this.checkSelectionState();
			this.syncGroupIndeterminate();
			this.emitReconciledNestedData();
		},
		handleCheckedPaymentMethod(checked, group) {
			group.isChecked = checked;
			group.isIndeterminate = false;
			(group.methods || []).forEach((child) => {
				child.isChecked = checked;
			});
			this.checkSelectionState();
			this.syncGroupIndeterminate();
			this.emitReconciledNestedData();
		},
		handleCheckedChildPaymentMethod(checked, child) {
			child.isChecked = checked;

			// Find parent group
			const group = this.list.find((g) => (g.methods || []).some((m) => m.id === child.id));
			if (group) {
				this.updateParentSelectionState(group);
			}

			this.checkSelectionState();
			this.syncGroupIndeterminate();
			this.emitReconciledNestedData();
		},
		handleToggleExpand(item) {
			item._isExpanded = !item._isExpanded;
			this.$nextTick(() => {
				if (this.$refs.scroller) {
					this.$refs.scroller.forceUpdate();
				}
				this.syncGroupIndeterminate();
			});
		},
		emitReconciledNestedData() {
			const result = this.list
				.map((group) => {
					const selectedMethods = (group.methods || []).filter((m) => m.isChecked);
					const isGroupChecked = group.isChecked;
					const isGroupIndeterminate = group.isIndeterminate;

					// Include in output if fully selected, or partially selected (has checked children)
					if (!isGroupChecked && !isGroupIndeterminate) return null;

					return {
						id: group.id,
						name: group.name,
						methods: selectedMethods.map((m) => ({ id: m.id, name: m.name })),
					};
				})
				.filter(Boolean);

			this.$emit('onChange', result);
		},
	},
};
</script>

<style lang="scss" scoped>
	.scroller {
		height: 100%;
	}

	.btn-add {
		min-width: rem(84);
	}

	.list {
		margin: 0 -#{rem(16)} -#{rem(16)};
	}

	.select-all {
		background-color: $color-gray-100;
		padding: rem(8) rem(16);
	}

	.list-select {
		min-height: rem(360);
		max-height: rem(360);
		list-style: none;
		padding: 0;
		margin: 0;

		.list-item {
			padding: rem(6) rem(16);

			label {
				margin-top: rem(3);
				cursor: pointer;
			}
		}
	}

	.expand-toggle {
		width: rem(20);
		display: flex;
		justify-content: center;
		align-items: center;
		height: rem(20);
	}

	.cursor-pointer {
		cursor: pointer;
	}

	.child-list {
		display: flex;
		flex-direction: column;
		gap: 4px;

		padding: 12px;
		margin: 8px 8px 0 16px;
		background-color: $color-gray-100;
		border-radius: 8px;
	}
</style>
